import {
  HttpErrorResponse,
  HttpEventType,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest
} from '@angular/common/http';
import { tap } from 'rxjs';

const CLIENT_APP_NAME = 'admin-core-frontend';
const REDACTED_VALUE = '[REDACTED]';
const SENSITIVE_KEYS = new Set([
  'password',
  'pass',
  'pwd',
  'token',
  'authorization',
  'refreshToken',
  'accessToken',
  'secret'
]);

function createRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `req-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

function getCurrentRoute(): string {
  if (typeof window === 'undefined') {
    return '/';
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}` || '/';
}

function getLoggedUserId(): string | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  const rawUser = localStorage.getItem('authUser');
  if (!rawUser) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawUser);
    const id = String(parsed?.pkUsuario ?? parsed?.id ?? '').trim();
    return id || null;
  } catch {
    return null;
  }
}

function isSameOriginRequest(url: string): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    const resolvedUrl = new URL(url, window.location.origin);
    return resolvedUrl.origin === window.location.origin;
  } catch {
    return true;
  }
}

function sanitizePayload(value: unknown): unknown {
  if (value == null) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizePayload(item));
  }

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const sanitizedEntries = Object.entries(record).map(([key, itemValue]) => {
      if (SENSITIVE_KEYS.has(key)) {
        return [key, REDACTED_VALUE];
      }

      return [key, sanitizePayload(itemValue)];
    });

    return Object.fromEntries(sanitizedEntries);
  }

  if (typeof value === 'string' && value.length > 500) {
    return `${value.slice(0, 500)}...`;
  }

  return value;
}

function summarizeRequest(req: HttpRequest<unknown>): Record<string, unknown> {
  return {
    method: req.method,
    url: req.urlWithParams,
    route: getCurrentRoute(),
    requestId: req.headers.get('X-Request-Id'),
    body: sanitizePayload(req.body)
  };
}

function getElapsedMs(startedAt: number): number {
  const now = typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

  return Math.round((now - startedAt) * 100) / 100;
}

export const requestTraceInterceptor: HttpInterceptorFn = (req, next: HttpHandlerFn) => {
  const route = getCurrentRoute();
  const isSameOrigin = isSameOriginRequest(req.url);
  const requestId = req.headers.get('X-Request-Id')?.trim() || createRequestId();
  const userId = getLoggedUserId();
  const startedAt = typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

  const traceHeaders = isSameOrigin
    ? {
        'X-Request-Id': requestId,
        'X-Client-App': CLIENT_APP_NAME,
        'X-Client-Route': route,
        'X-Client-Timestamp': new Date().toISOString(),
        ...(userId ? { 'X-Client-User': userId } : {})
      }
    : {};

  const tracedRequest = req.clone({
    setHeaders: traceHeaders
  });

  console.info('[HTTP][OUT]', {
    ...summarizeRequest(tracedRequest),
    sameOrigin: isSameOrigin
  });

  return next(tracedRequest).pipe(
    tap({
      next: (event) => {
        if (event.type !== HttpEventType.Response) {
          return;
        }

        const responseRequestId = event.headers.get('X-Request-Id') || requestId;
        console.info('[HTTP][OK]', {
          method: tracedRequest.method,
          url: tracedRequest.urlWithParams,
          route,
          requestId: responseRequestId,
          status: event.status,
          server: event.headers.get('server') || null,
          durationMs: getElapsedMs(startedAt)
        });
      },
      error: (error: unknown) => {
        const baseLog = {
          method: tracedRequest.method,
          url: tracedRequest.urlWithParams,
          route,
          requestId,
          durationMs: getElapsedMs(startedAt)
        };

        if (error instanceof HttpErrorResponse) {
          console.error('[HTTP][ERR]', {
            ...baseLog,
            status: error.status,
            statusText: error.statusText,
            message: error.message,
            error: sanitizePayload(error.error)
          });
          return;
        }

        console.error('[HTTP][ERR]', {
          ...baseLog,
          error
        });
      }
    })
  );
};
